import { useEffect, useRef } from 'react'
import { useLazyGetOrder } from '../../graphQL/queries/order'
import {
  IOrderResponse,
  IOrderVariables,
} from '../../graphQL/queries/order/types'
import {
  clearOrderDetail,
  setOrderDetail,
  setOrderDetailError,
  setOrderDetailLoading,
  setOrderDetailNotFound,
  setOrderDetailPollError,
  setOrderDetailPolling,
  setOrderDetailPollResult,
  setOrderDetailSessionEnded,
  setOrderDetailTimedOut,
} from '../../redux/slices/orders'
import { ORDER_POLL_FIRST_LOOK_MS } from '../../redux/slices/orders/types'
import {
  isOrderPending,
  nextPollDelay,
  resolveOrderConfirmation,
} from '../../utils/orderConfirmation'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import { handleOrdersFetchError, isNotFoundError } from './ordersHookHelpers'

const FUNCTION_NAME = 'useGetOrderDetail'

/**
 * Fetches one order and writes it into the detail half of `ordersSlice`.
 *
 * Poll timings come from `configuration.order.confirmation` on the session
 * config — `pollingTime`, `maxAttempts` and `timeout` — and fall back to
 * `ORDER_POLL_INTERVAL_MS` / `ORDER_POLL_TIMEOUT_MS` when the block is absent,
 * which is every storefront that has not opted in. `required` is **not** read
 * here: whether to re-read an order the server says is still in progress is
 * not a per-storefront choice, and gating the fetch on it would leave a
 * pending order frozen on screen. What `required` gates is the visible
 * confirming state — see `selectOrderDetailPanelStatus`.
 *
 * Currency is absent here for the same reason it is absent from `useGetOrders`:
 * `Order` carries no currency field, so the container reads
 * `state.defaultSlice.config?.options?.currencyPrefix` at render time.
 *
 * Refresh is deliberately **not** among the returned functions. The panel's
 * refresh action dispatches `refreshOrderDetail()` and this hook watches the
 * resulting nonce, because handing the caller a function would mean the caller
 * holding an instance of this hook — and the instance an `EventManager`
 * handler holds is never the one polling.
 */
export const useGetOrderDetail = () => {
  const dispatch = useAppDispatch()
  const sessionUid = useAppSelector((state) => state.defaultSlice.sessionId)
  const storefrontName = useAppSelector((state) => state.defaultSlice.storeName)
  const orderConfirmation = useAppSelector(
    (state) =>
      state.defaultSlice.sessionData?.configuration?.order?.confirmation,
  )
  /**
   * The refresh edge, read from the store rather than from a callback.
   *
   * The panel's refresh action reaches Redux the way every other authored
   * action does — through `EventManager` — but it cannot reach *this hook*
   * that way, because a handler calling `useGetOrderDetail` would hold a
   * different instance from the one polling and would arm a rival chain
   * against the same slice. The dispatch crosses instances, the refs below do
   * not. See `detailRefreshNonce` on the slice for the full reasoning.
   */
  const refreshNonce = useAppSelector(
    (state) => state.ordersSlice.detailRefreshNonce,
  )

  // `network-only` because the whole point of polling is to see the order
  // change; the normalised cache would replay the first response forever.
  const [getOrderFn] = useLazyGetOrder({ fetchPolicy: 'network-only' })

  /**
   * Monotonic token identifying the newest in-flight request, as in
   * `useGetOrders`.
   *
   * Its real job is the checks taken *after* an await: a response that arrives
   * for a superseded request is dropped rather than written into the slice.
   * That is the only case the timer cannot cover, because a request already in
   * flight cannot be cancelled — Apollo resolves it regardless of what the
   * component does next. Opening a second order, resetting, and unmounting all
   * bump the token for that reason.
   */
  const requestIdRef = useRef(0)
  /**
   * The one scheduled tick, and the actual re-entrancy guard for polling.
   *
   * A tick is scheduled only after the previous one has resolved, so the chain
   * holds at most one pending timer and ticks cannot overlap. `isPollingOrder`
   * on the slice is a display flag and guards nothing — the list half's
   * `isLoadingMore` works the other way round, which is an easy trap.
   */
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * When the current poll started, against which `timeout` is measured.
   *
   * A start time and not an absolute deadline, because the deadline is not
   * knowable when the fetch begins. On a fresh load the first request goes out
   * before `getSession` has resolved, so `Date.now() + timeout` computed here
   * would pin the fallback 60s for the life of the screen and no configured
   * `timeout` would ever apply — a storefront asking for 300s would report a
   * timeout at 60s over an order still legitimately confirming. Stored raw and
   * compared against `pollSettingsRef.current.timeout` at each tick, so a block
   * that lands mid-flight still governs.
   */
  const pollStartedAtRef = useRef(0)
  /**
   * Ticks issued for the current order, against `maxAttempts`.
   *
   * A second bound beside the deadline, and not a duplicate of it: the
   * deadline is wall clock and is only tested between ticks, so a run of slow
   * requests spends the budget without spending the clock. Whichever bound is
   * reached first ends the poll. Reset per fetch, like the start time.
   */
  const pollAttemptsRef = useRef(0)
  /**
   * The confirmation parameters, re-read on every render and never captured.
   *
   * A ref rather than the closure value because the first fetch runs before
   * `getSession` has resolved: `sessionId` comes off the URL and `Config`
   * dispatches it in the same effect that fires `getSession`, without gating
   * the render on either. So the screen is fetchable a beat before the config
   * that governs its poll arrives, and on a fresh load the first
   * `fetchOrderDetail` always runs while `pollSettingsRef.current` still holds
   * the fallbacks. That is the ordinary sequence, not an edge case.
   *
   * A ref only helps where its value is read *after* that window closes, so
   * every read here is taken at the latest possible moment: all three come off
   * `pollSettingsRef.current` together inside `pollTick`, after the request
   * that tick follows has resolved. Destructuring any of them earlier
   * reinstates the bug this ref exists to avoid, and does it silently: the
   * values are real, just stale.
   *
   * Since COM-875 the chain-*opening* arm reads none of them — it schedules at
   * the hardcoded `ORDER_POLL_FIRST_LOOK_MS`, which is immune to this race by
   * construction rather than by careful sequencing. That narrows what this ref
   * protects to the cadence and the deadline; it does not retire it.
   */
  const pollSettingsRef = useRef(resolveOrderConfirmation(orderConfirmation))
  pollSettingsRef.current = resolveOrderConfirmation(orderConfirmation)
  /**
   * The order *this instance* last fetched.
   *
   * The nonce is in the store, so every mounted `useGetOrderDetail` sees the
   * same edge. This does not pick a winner among them — it only rejects the
   * ones that have never fetched, which is every instance an `EventManager`
   * handler or any other bystander would hold. That an order is refreshed once
   * rather than twice still rests on `useOrderDetail` being the sole caller of
   * `fetchOrderDetail`; what this guarantees is that merely *mounting* the
   * hook somewhere else cannot start a rival chain.
   *
   * Read from a ref and not from `selected.id` on the slice, which would give
   * up even that: a store read would hand every instance an order id, and the
   * bystanders would each act on the edge. It is also null on a `notFound`
   * miss, where the retry has to know which order it is retrying.
   */
  const lastOrderIdRef = useRef('')
  /**
   * True while this instance owns a live poll chain — armed, ticking, or
   * awaiting a tick's response.
   *
   * Not `isPollingOrder` on the slice, which is a display flag saying a single
   * request is in flight and is false between ticks; and not derivable from
   * `pollTimerRef`, which is null for the whole of a tick's request. This is
   * ownership of the chain, held from the moment an order is seen to be
   * pending until it settles, spends its budget, or is torn down.
   *
   * Read for one decision: whether a re-read of this order should behave like
   * a poll tick, which keeps the order on screen through a failure, or like a
   * first load, which does not. See `fetchOrderDetail`.
   */
  const pollChainLiveRef = useRef(false)
  /**
   * The nonce this instance has already acted on.
   *
   * The dependency array alone is not enough. `index.tsx` renders under
   * `StrictMode`, where React 18 mounts, tears down and re-runs every effect
   * on the same instance with its refs intact — so the refresh effect below
   * runs a second time with an unchanged nonce, by which point
   * `lastOrderIdRef` has been set by the mount fetch and the guard above no
   * longer stops it. Comparing the value makes the effect fire on a genuine
   * change and nothing else.
   */
  const seenRefreshNonceRef = useRef(refreshNonce)

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  // Refs only, so no dependencies and no stale-closure hazard: a screen left
  // mid-poll must not keep firing requests at an unmounted component.
  //
  // The token is bumped as well as the timer cleared, for the same reason
  // `resetOrderDetail` does both. Clearing the timer alone leaves a tick that
  // was already awaiting a response free to dispatch into the slice on the way
  // out and then schedule the next tick from its own tail — re-arming the
  // chain the cleanup just tore down, with nothing left mounted to stop it.
  useEffect(
    () => () => {
      stopPolling()
      requestIdRef.current += 1
    },
    [],
  )

  /**
   * `sessionUid` always travels with `id`. See the block comment on the query
   * document for the security property and why an *omitted* session is the
   * shape that reaches the unscoped branch.
   *
   * Hence the cast rather than a `|| ''` default: defaulting would paper over
   * a missing session at exactly the site that must not tolerate one. The
   * guard in `fetchOrderDetail` is the only way in, and `pollTick` is
   * reachable only from there, so no session can be absent by the time
   * execution arrives here.
   *
   * `useGetOrders` writes `sessionUid || ''` — not because its `$sessionUid:
   * ID!` makes `''` safe (it satisfies `ID!` perfectly well) but because
   * `getOrders` has no unscoped branch for a falsy session to fall into.
   */
  const buildVariables = (orderId: string): IOrderVariables => ({
    storefrontName: storefrontName || '',
    sessionUid: sessionUid as string,
    id: orderId,
  })

  /**
   * Runs the query and normalises the two ways Apollo reports a failure, the
   * way `useGetOrders.runQuery` does.
   */
  const runQuery = async (variables: IOrderVariables) => {
    const result = await getOrderFn({ variables })
    if (result.error) throw result.error
    return result.data as IOrderResponse | undefined
  }

  /** True when a newer request started while this one was in flight. */
  const isSuperseded = (requestId: number) => requestIdRef.current !== requestId

  /**
   * One poll tick, which schedules the next one unless the order has settled.
   *
   * No failure tears the screen down: every one of them records a `pollError`
   * and ticks again, because the order on screen is still the last thing the
   * server told us and a single dropped request does not make it wrong.
   */
  const pollTick = async (
    orderId: string,
    requestId: number,
  ): Promise<void> => {
    // This tick's timer has fired, so the ref no longer names anything that
    // can be cleared. Leaving the spent id in place is what would make a later
    // `stopPolling` look effective while cancelling nothing.
    pollTimerRef.current = null
    // Unreachable while `stopPolling` exists — every path that bumps the token
    // clears the timer in the same breath, so a superseded tick never gets to
    // start. Defence in depth: do not delete it as dead code, and do not rely
    // on it either. The checks after the awaits are the ones that fire.
    if (isSuperseded(requestId)) return

    dispatch(setOrderDetailPolling())
    // Counted before the request, so `maxAttempts: 1` means exactly one tick.
    pollAttemptsRef.current += 1
    try {
      const data = await runQuery(buildVariables(orderId))
      if (isSuperseded(requestId)) return

      const order = data?.order
      if (order) {
        dispatch(setOrderDetailPollResult(order))
        // Settled — CONFIRMED, REJECTED, ERROR or the server's own TIMEOUT.
        if (!isOrderPending(order)) {
          pollChainLiveRef.current = false
          return
        }
      } else {
        // Not the miss case: a miss raises a 404 and is classified in the
        // catch below. A 200 with no body is a transport anomaly, so it is
        // reported as an ordinary failed tick.
        dispatch(setOrderDetailPollError('ORDER_POLL_FAILED'))
      }
    } catch (exception) {
      if (isSuperseded(requestId)) return
      dispatch(
        // A 404 mid-poll means the order stopped being readable under us,
        // which is a different fact from the request failing. Both keep the
        // order on screen and keep ticking; only the recorded reason differs.
        setOrderDetailPollError(
          isNotFoundError(exception) ? 'ORDER_POLL_EMPTY' : 'ORDER_POLL_FAILED',
        ),
      )
      handleOrdersFetchError(exception, FUNCTION_NAME, 'order poll tick failed')
    }

    const { pollingTime, maxAttempts, timeout } = pollSettingsRef.current
    // One sample, read by the deadline test and by the delay below. Two would
    // let the clock advance between them and hand `nextPollDelay` a negative
    // remainder — see that function for why it carries no clamp.
    const elapsed = Date.now() - pollStartedAtRef.current
    if (elapsed >= timeout || pollAttemptsRef.current >= maxAttempts) {
      // The client gave up. Distinct from the order carrying
      // `externalRefStatus: 'TIMEOUT'`, which is the server's own verdict.
      //
      // The elapsed clock is sampled between ticks, never during one: nothing
      // bounds a single request, so a hung one postpones this check
      // indefinitely rather than tripping it. TODO(COM-772) adds a request
      // timeout on the Apollo link and makes this deadline hold regardless.
      // `maxAttempts` is unaffected by that — it counts ticks, not time — which
      // is the other half of why both bounds exist.
      pollChainLiveRef.current = false
      dispatch(setOrderDetailTimedOut())
      return
    }

    pollChainLiveRef.current = true
    // `nextPollDelay` rather than `pollingTime`, so the tick that would
    // overshoot the deadline lands *on* it instead (COM-871). It is the chain's
    // last look and it is a real one: this dispatch happens before the budget
    // check above, so the result is read. Still a `setTimeout` on
    // `pollTimerRef`, so `stopPolling()` cancels it like any other tick.
    pollTimerRef.current = setTimeout(
      () => {
        pollTick(orderId, requestId)
      },
      nextPollDelay(pollingTime, timeout, elapsed),
    )
  }

  /**
   * Loads one order. Used on mount, when the route's id changes, and for the
   * refresh action's re-read.
   *
   * If the order comes back still `PROCESSING`, polling is armed for it.
   */
  const fetchOrderDetail = async (orderId: string) => {
    // Nothing to fetch, and nothing to say: a detail route always carries an
    // id, so an empty one is a caller bug rather than anything the shopper can
    // see or act on. Left silent, and kept separate from the session guard
    // below so the two are never mistaken for the same failure.
    if (!orderId) return

    /*
     * Is this a re-read of the very order this instance is already polling,
     * rather than the opening of a new one? Decided before `lastOrderIdRef` is
     * overwritten, because the comparison is against the *previous* value.
     *
     * Only the refresh action can make this true. A mount fetch has no chain,
     * and a route change to a different order fails the id comparison — which
     * it must: opening order B has to blank order A, or a frame renders A's
     * totals under B's header.
     */
    const isReReadOfPolledOrder =
      pollChainLiveRef.current && orderId === lastOrderIdRef.current

    // Recorded before the session guard rather than after it. Recorded after,
    // a fetch that stopped at the guard would leave this instance owning no
    // order, and every later refresh would be a silent no-op — the shopper
    // pressing the control on the `sessionEnded` state would get nothing at
    // all. See that guard for how far the recovery it allows is real.
    lastOrderIdRef.current = orderId

    /*
     * Tear the old chain down before deciding anything else, the session guard
     * included.
     *
     * Below the guard, as this sat until COM-779's review, a refresh taken
     * while a chain was live and the session had gone neither cancelled the
     * armed tick nor bumped the token past it: the panel showed `sessionEnded`
     * for one frame, the orphaned tick wrote `setOrderDetailPollResult` back
     * over it, and the chain went on arming for the life of the screen —
     * issuing scoped reads under a `sessionUid` its closure still held and the
     * store had discarded. A caller asking for a fresh read is entitled to the
     * old chain stopping whether or not the read can proceed.
     */
    stopPolling()
    const requestId = (requestIdRef.current += 1)

    // No session means the request would go out unscoped — see the block
    // comment on the query document. Refusing to send it is the safe failure,
    // but it has to be a visible one: returning silently would leave
    // `selectedStatus` at `'idle'`, which the panel reads as `loading`, and
    // the shopper would sit under a skeleton that never resolves.
    if (!sessionUid) {
      pollChainLiveRef.current = false
      dispatch(setOrderDetailSessionEnded())
      return
    }

    // The clock starts when the first request goes out, so a slow first fetch
    // spends the shopper's budget the way a slow tick does. Only the start is
    // fixed here; what it is measured against is read per tick. Reset for the
    // re-read below as well: a shopper asking to look again is asking for a
    // fresh budget, not for a spent one to resume.
    pollStartedAtRef.current = Date.now()
    pollAttemptsRef.current = 0

    /*
     * A re-read of an order that is on screen and still confirming goes
     * through the poll's own path, not the first-load path.
     *
     * The difference is what a *failure* costs. `setOrderDetailLoading` nulls
     * `selected`, and a first load that then fails reports `error` and arms
     * nothing — so pressing refresh on a healthy confirming order and losing
     * one request would take the order off screen and kill the poll that was
     * about to resolve it. `pollTick` takes the opposite stance for exactly
     * this case and says why: the order on screen is still the last thing the
     * server told us, and a single dropped request does not make it wrong. The
     * same dropped request must not be survivable when the poll takes it and
     * fatal when the shopper asks for it.
     *
     * Confined to the polling case on purpose. Everywhere else the fresh load
     * is right: `error`, `notFound` and `sessionEnded` hold no order to
     * protect, and `timeout` needs `setOrderDetailLoading` to clear
     * `pollTimedOut` — which `setOrderDetailPollResult` does not touch, so a
     * re-read in place would leave a successfully refreshed order still
     * rendering as timed out. Its body is hidden in that state anyway, so
     * there is nothing on screen to lose.
     */
    if (isReReadOfPolledOrder) {
      await pollTick(orderId, requestId)
      return
    }

    pollChainLiveRef.current = false
    dispatch(setOrderDetailLoading())

    try {
      const data = await runQuery(buildVariables(orderId))
      if (isSuperseded(requestId)) return

      const order = data?.order
      // Not the miss case, despite the shape. A miss raises a 404 and is
      // classified in the catch below; a 200 carrying no order is a transport
      // anomaly, so it reports as a plain error and offers the retry.
      if (!order) {
        dispatch(setOrderDetailError())
        return
      }

      dispatch(setOrderDetail(order))
      if (isOrderPending(order)) {
        pollChainLiveRef.current = true
        /*
         * The **first** look, at `ORDER_POLL_FIRST_LOOK_MS` rather than at
         * `pollingTime` (COM-875) — the same defect as the confirmation hook's
         * `armPoll`, on the read surface, and given the same answer because
         * this is the same position: the request above has just read the
         * order, not settled it. `pollTick`'s own tail still schedules at
         * `pollingTime`, so the cadence from the second look on is unchanged.
         *
         * A constant here also makes this site *immune* to the stale-config
         * race `pollSettingsRef` exists for, rather than merely careful about
         * it — see that ref's docstring.
         */
        pollTimerRef.current = setTimeout(() => {
          pollTick(orderId, requestId)
        }, ORDER_POLL_FIRST_LOOK_MS)
      }
    } catch (exception) {
      if (isSuperseded(requestId)) return
      // Where this parts company with the list half: an empty page of history
      // is a valid empty state, but an order the server will not hand over is
      // a miss, and the panel renders it differently from a failed request.
      dispatch(
        isNotFoundError(exception)
          ? setOrderDetailNotFound()
          : setOrderDetailError(),
      )
      handleOrdersFetchError(exception, FUNCTION_NAME, 'order fetch failed')
    }
  }

  /**
   * The Detail screen's refresh action, taken from the store.
   *
   * Re-runs `fetchOrderDetail` for the order this instance already has, which
   * is what makes the hazards fall out rather than needing handling here:
   *
   * - a tick in flight is superseded, because `fetchOrderDetail` clears the
   *   scheduled timer and bumps the request token before it decides anything
   *   else — the session guard included, which is where that used to be false.
   *   One chain, and it is the new one;
   * - an order that has settled is not re-armed, because arming is decided by
   *   `isOrderPending` on the *response*, not by anything remembered here;
   * - the deadline and the attempt budget start again, which is the point: a
   *   shopper who asked to re-check a timed-out order is asking for a new
   *   budget, not for the spent one to be resumed;
   * - an order still confirming is re-read in place rather than reloaded, so a
   *   dropped request costs the shopper neither the order nor the poll.
   *
   * No dependency but the nonce. `fetchOrderDetail` is re-created every
   * render and closes over nothing that a stale copy would get wrong — the
   * poll settings are read through a ref, and `sessionUid` is captured at the
   * moment the effect runs, which is the moment the shopper pressed refresh.
   */
  useEffect(() => {
    if (seenRefreshNonceRef.current === refreshNonce) return
    seenRefreshNonceRef.current = refreshNonce

    // Nothing fetched by this instance, so nothing of its own to refresh.
    // `fetchOrderDetail` would refuse an empty id anyway; this states the
    // ownership rule where it is decided rather than leaving it to be inferred
    // from a guard three functions away.
    if (!lastOrderIdRef.current) return

    fetchOrderDetail(lastOrderIdRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce])

  /**
   * Stops the poll and resets the detail half, for a caller holding this very
   * instance.
   *
   * **Nothing in production calls it.** Leaving the screen goes through
   * `useOrderActions.backToHistory`, which dispatches `clearOrderDetail()`
   * itself precisely because its instance is not this one, and the poll is
   * stopped by the unmount cleanup rather than by anything here. So this is
   * the complete teardown expressed in one place, exercised by tests and
   * available to a future caller that does hold the polling instance — not the
   * live protection on the leave-screen path.
   *
   * The token is bumped as well as the timer cleared, so a tick already
   * awaiting a response cannot write the order back after the reset; the
   * remembered order and the chain flag go with them, so a refresh dispatched
   * afterwards cannot re-fetch what was just reset.
   */
  const resetOrderDetail = () => {
    stopPolling()
    requestIdRef.current += 1
    lastOrderIdRef.current = ''
    pollChainLiveRef.current = false
    dispatch(clearOrderDetail())
  }

  return { fetchOrderDetail, resetOrderDetail }
}

export default useGetOrderDetail
